import * as Dialog from '@radix-ui/react-dialog';
import { Suspense, lazy, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { CSSProperties, FocusEvent, PointerEvent as ReactPointerEvent, PropsWithChildren, ReactNode } from 'react';
import { flushSync } from 'react-dom';

import type { PanelAccessModel, PanelDestination, PanelNavigationRole } from './access';
import { PANEL_DESTINATION_GROUPS, bottomBarDestinations, navigationScope, roleDestinations, visibleDestinations } from './access';
import { PANEL_V2_RESPONSIVE_MEDIA, useMediaQuery } from './adaptive';
import { markModal } from './acceso-parts';
import { paseOptedIn, subscribePaseOptIn } from './pase/opt-in';
import { ReauthenticationForm } from './credentials';
import { prefetchScreen, prefetchScreensWhenIdle } from './screen-prefetch';
import { prefersReducedMotion } from './motion';
import { DialogCard } from './living/dialog-card';
import { MotionProvider } from './living/motion-provider';
import { NavigationTone, ShellToneContext } from './living/tone';
import type { LivingTone, ShellToneRequest } from './living/tone';
import { usePanelSessionInboxPresentation } from './inbox/presentation';
import {
  DESTINATION_ICON_PARTS, DestinationMark, LocalViewMark, MoreMark, NavigationMark, NavigationToggleMark, PARTE_ICON_PARTS,
} from './navigation-marks';
import type { DestinationIconPart } from './navigation-marks';
import { readDeviceChoice, writeDeviceChoice } from '../runtime/durable-work/device-choice';
import { NoticeInbox } from './notice-inbox';
import type { AuthenticationOutcome, ReauthenticationRequest } from './runtime-port';
import { StaffAvatar } from './staff-avatar';
import { FrozenInteractionBoundary, SystemBanner } from './state';
import './session-boundary.scss';

export interface IdentityChromeProps {
  readonly personName: string;
  /** The signed-in person's avatar; initials stand in without one. */
  readonly avatarUrl?: string | null;
  /** Optional compact label; the account menu still displays the full identity. */
  readonly summaryName?: string;
  readonly identifier?: string;
  readonly restaurantName: string;
  readonly roleLabel?: string;
  readonly onChangePerson: () => void;
  readonly onChangeRestaurant?: () => void;
  readonly onOpenAccountSettings?: () => void;
  readonly onLogout: () => void;
  /** Platform back-office sessions have neither restaurant switching nor a restaurant inbox. */
  readonly showRestaurantSwitcher?: boolean;
  readonly showChangePerson?: boolean;
  readonly showNotices?: boolean;
  /** Set by the shell in its icon rail, which names each control beside it: the person and the role for the account,
   * and its own label for the bell in place of the browser's tooltip. */
  readonly railTips?: boolean;
}

export function IdentityChrome({
  personName,
  avatarUrl,
  summaryName,
  identifier,
  restaurantName,
  roleLabel = 'Equipo',
  onChangePerson,
  onChangeRestaurant,
  onOpenAccountSettings,
  onLogout,
  showRestaurantSwitcher = true,
  showChangePerson = true,
  showNotices = true,
  railTips = false,
}: IdentityChromeProps) {
  const sessionInbox = usePanelSessionInboxPresentation();
  const accountMenu = useRef<HTMLDetailsElement>(null);
  const accountSummary = useRef<HTMLElement>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const avatarLetter = Array.from(personName.trim())[0]?.toLocaleUpperCase('es-ES') ?? '·';
  const closeAccountMenu = (restoreFocus = false) => {
    accountMenu.current?.removeAttribute('open');
    setAccountOpen(false);
    if (restoreFocus) accountSummary.current?.focus();
  };

  useEffect(() => {
    if (!accountOpen) return undefined;
    const closeFromOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !accountMenu.current?.contains(event.target)) closeAccountMenu();
    };
    const closeFromEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeAccountMenu(true);
    };
    document.addEventListener('pointerdown', closeFromOutside);
    document.addEventListener('keydown', closeFromEscape);
    return () => {
      document.removeEventListener('pointerdown', closeFromOutside);
      document.removeEventListener('keydown', closeFromEscape);
    };
  }, [accountOpen]);

  return (
    <div className="identity-chrome">
      <details className="identity-chrome__account" onToggle={(event) => setAccountOpen(event.currentTarget.open)} ref={accountMenu}>
        <summary aria-expanded={accountOpen} aria-haspopup="menu" aria-label="Tu perfil y tu sesión" className="identity-chrome__summary"
          data-rail-tip={railTips ? `${personName} · ${roleLabel}` : undefined} ref={accountSummary}>
          <StaffAvatar className="identity-chrome__avatar" fallback={avatarLetter} imageUrl={avatarUrl} />
          <span className="identity-chrome__person">
            <strong className="identity-chrome__name">{summaryName ?? personName}</strong>
            <span className="identity-chrome__role">{roleLabel}</span>
          </span>
        </summary>
        <div className="identity-chrome__menu" role="menu">
          <div className="identity-chrome__menu-head">
            {identifier ? <><div><strong>{personName}</strong><span> · {roleLabel}</span></div><span>{identifier}</span></>
              : <><strong>{personName}</strong><span className="restaurant-name">{restaurantName}</span></>}
          </div>
          {showChangePerson && <button className="identity-chrome__menu-item" onClick={() => { closeAccountMenu(); onChangePerson(); }} role="menuitem" type="button">
            Cambiar de persona
          </button>}
          {onOpenAccountSettings === undefined ? (
            <a className="identity-chrome__menu-item" href="/app/config" role="menuitem">Ajustes de cuenta</a>
          ) : (
            <button className="identity-chrome__menu-item" onClick={() => { closeAccountMenu(); onOpenAccountSettings(); }} role="menuitem" type="button">
              Ajustes de cuenta
            </button>
          )}
          {showRestaurantSwitcher && <button
            className="identity-chrome__menu-item"
            disabled={onChangeRestaurant === undefined}
            onClick={() => { closeAccountMenu(); onChangeRestaurant?.(); }}
            role="menuitem"
            title={onChangeRestaurant === undefined ? 'No disponible en esta sesión.' : undefined}
            type="button"
          >
            Cambiar de restaurante
          </button>}
          <button className="identity-chrome__menu-item identity-chrome__menu-item--logout" onClick={() => { closeAccountMenu(); onLogout(); }} role="menuitem" type="button">
            Cerrar sesión
          </button>
        </div>
      </details>
      {showNotices && <NoticeInbox {...(sessionInbox === null ? {} : { content: sessionInbox.content, knownUnreadCount: sessionInbox.knownUnreadCount })}
        {...(railTips ? { tooltip: false } : {})} />}
    </div>
  );
}

/**
 * One navigation row, whatever it opens. Destinations are canonical grants;
 * El Parte is the Home local view. Keeping both behind one shape lets the two
 * modes of D-160 ruling 17 share the wide list, the rail and the sheet menu
 * without a second list component — and without letting a local view anywhere
 * near a `PanelDestinationId`.
 */
interface NavigationEntry {
  readonly key: string;
  readonly label: string;
  readonly shortLabel: string;
  readonly path: string;
  readonly group: string;
  readonly parts: readonly DestinationIconPart[];
}

/** v20 draws the Home section above the rest and labels the group «Home». */
const HOME_NAVIGATION_GROUP = 'Home';

const destinationEntry = (destination: PanelDestination): NavigationEntry => ({
  key: destination.workspaceId,
  label: destination.label,
  shortLabel: destination.shortLabel,
  path: destination.path,
  group: destination.group,
  parts: DESTINATION_ICON_PARTS[destination.workspaceId],
});

/* v20 `chromeVals` row `parte` and `NAV_SHORT.parte`: one label at both widths.
   D-160 ruling 1 makes Home's whole content El Parte, so the row opens the
   Home local view's own path rather than a route of its own. */
const parteEntry = (home: PanelLocalView): NavigationEntry => ({
  key: 'PARTE',
  label: 'El Parte',
  shortLabel: 'El Parte',
  path: home.path,
  group: HOME_NAVIGATION_GROUP,
  parts: PARTE_ICON_PARTS,
});

/** A destination pressed or focused starts loading its screen before the click lands. */
const prefetchOnIntent = (path: string) => ({ onFocus: () => prefetchScreen(path), onPointerDown: () => prefetchScreen(path) });

interface NavigationListProps {
  readonly currentPath: string;
  readonly entries: readonly NavigationEntry[];
  readonly groups: readonly string[];
  readonly className?: string;
  readonly closeAfterNavigate?: boolean;
  readonly compact?: boolean;
  readonly grouped?: boolean;
  readonly onNavigate?: (path: string) => void;
}

function NavigationList({ currentPath, entries, groups, className = '', closeAfterNavigate = false, compact = false, grouped = false, onNavigate }: NavigationListProps) {
  const itemFor = (entry: NavigationEntry, index: number, visible: readonly NavigationEntry[]) => {
    const startsGroup = !grouped && index > 0 && visible[index - 1]?.group !== entry.group;
    const link = (
      <a
        aria-current={currentPath === entry.path ? 'page' : undefined}
        aria-label={entry.label}
        className="app-navigation__link"
        href={entry.path}
        onClick={(event) => { if (onNavigate && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onNavigate(entry.path); } }}
        {...prefetchOnIntent(entry.path)}
      >
        <NavigationMark parts={entry.parts} />
        <span className="app-navigation__label">{compact ? entry.shortLabel : entry.label}</span>
      </a>
    );
    return <li data-navigation-group-start={startsGroup || undefined} key={entry.key}>{closeAfterNavigate ? <Dialog.Close asChild>{link}</Dialog.Close> : link}</li>;
  };

  if (!grouped) return <ul className={`app-navigation__list ${className}`.trim()}>{entries.map(itemFor)}</ul>;

  return (
    <div className={`app-navigation__groups ${className}`.trim()}>
      {groups.map((group) => {
        const groupEntries = entries.filter((entry) => entry.group === group);
        if (groupEntries.length === 0) return null;
        return (
          <section aria-label={group} className="app-navigation__group" key={group}>
            <h2 className="app-navigation__group-label">{group}</h2>
            <ul className="app-navigation__list">{groupEntries.map(itemFor)}</ul>
          </section>
        );
      })}
    </div>
  );
}

/** The name the icon rail draws beside the control under the pointer or holding keyboard focus. */
interface RailTip {
  readonly label: string;
  readonly left: number;
  readonly top: number;
}

/** What the icon rail can name: anything that says so itself, and every control with an accessible name. */
const RAIL_TIP_TARGETS = '[data-rail-tip], a[aria-label], button[aria-label], summary[aria-label]';

/** A click also focuses what it presses; only the keyboard's focus asks for the name. */
const focusedByKeyboard = (element: Element): boolean => {
  try { return element.matches(':focus-visible'); } catch { return true; }
};

/** Local views are not workspaces and must never mint canonical grants. */
export interface PanelLocalView {
  readonly id: 'home' | 'board';
  readonly label: 'Home' | 'Board';
  readonly path: '/app/home' | '/app/board' | '/app/analytics';
}

/**
 * The Home | Board switch. D-160 ruling 17 keeps «Board» lit on every
 * destination screen, where the Board page itself is *not* open, so the lit
 * side and the open page are two different states:
 *
 * - `aria-current="page"` stays on the exact path match only. Assistive tech is
 *   never told the Board page is open while Menú or TPV is.
 * - the lit side of the switch carries `aria-current="true"` — «the current
 *   item in this set», which is what a segmented control means — plus
 *   `data-active`, which is what the stylesheet paints.
 */
/** Where the switch's thumb last rested. Home and Board mount a shell of their own, so going from Home to a destination
 * draws a new switch: it starts its thumb here and lets it travel, as it would have inside one shell. */
let lastLitView: PanelLocalView['id'] | undefined;

function LocalViewLinks({ views, currentPath, litViewId, onNavigate, closeAfterNavigate = false, travels = false }: {
  readonly views: readonly PanelLocalView[]; readonly currentPath: string;
  readonly litViewId: PanelLocalView['id'] | undefined;
  readonly onNavigate?: (path: string) => void; readonly closeAfterNavigate?: boolean;
  /** The navigation's own switch remembers where its thumb was; the drawer's is drawn in place. */
  readonly travels?: boolean;
}) {
  const [thumb, setThumb] = useState(() => (travels ? lastLitView ?? litViewId : litViewId));
  useEffect(() => {
    if (travels) lastLitView = litViewId;
    if (thumb === litViewId) return undefined;
    // One frame where it was, then the stylesheet's spring takes it to the lit side.
    const frame = window.requestAnimationFrame(() => setThumb(litViewId));
    return () => window.cancelAnimationFrame(frame);
  }, [litViewId, thumb, travels]);
  if (!views.length) return null;
  // Two views are a switch: a track and a thumb that slides to the lit side. One view (an employee's Board) is a plain link.
  const isSwitch = views.length > 1;
  return <div className="app-navigation__views" aria-label="Vistas" data-switch={isSwitch ? '' : undefined} data-thumb={isSwitch ? thumb : undefined}>
    {isSwitch && <span aria-hidden="true" className="app-navigation__thumb" />}
    {views.map((view) => {
      // A Board that opens a destination (a manager's Analítica) is never the open page: the destination's row is.
      const open = currentPath === view.path && view.path !== MANAGER_BOARD_PATH;
      const lit = litViewId === view.id;
      const link = <a aria-current={open ? 'page' : lit ? 'true' : undefined} aria-label={view.label} className="app-navigation__link"
        data-active={lit ? '' : undefined} href={view.path}
        onClick={(event) => { if (onNavigate && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onNavigate(view.path); } }}
        {...prefetchOnIntent(view.path)}>
        <LocalViewMark id={view.id} /><span className="app-navigation__label">{view.label}</span>
      </a>;
      return closeAfterNavigate ? <Dialog.Close asChild key={view.id}>{link}</Dialog.Close> : <span key={view.id}>{link}</span>;
    })}
  </div>;
}

/* The Day/Night switch (docs/decisions/2026-10-31-panel-day-and-night-modes.md) is drawn only while a screen that opted
   into night is mounted, and loads in a chunk of its own, so the entry carries only its empty place. */
const ThemeSwitch = lazy(() => import('./pase/theme-switch'));
const paseNotOptedIn = () => false;

function ModeSwitch({ compact = false }: { readonly compact?: boolean }) {
  return <Suspense fallback={<i className="pa-theme-slot" />}><ThemeSwitch compact={compact} /></Suspense>;
}

/** @internal Composed by `NavigationShell`; exported for its spec. */
export interface ResponsiveNavigationProps {
  readonly currentPath: string;
  readonly destinations: readonly PanelDestination[];
  /** Folded: the rail of icons. Unfolded: the same rail with its names beside it, where the window has room for them. */
  readonly folded: boolean;
  readonly onFoldChange: (folded: boolean) => void;
  readonly hasPrimaryAction: boolean;
  readonly navigationRole: PanelNavigationRole;
  readonly restaurantName?: string;
  readonly identity?: IdentityChromeProps;
  readonly localViews?: readonly PanelLocalView[];
  readonly onNavigate?: (path: string) => void;
  readonly drawerClassName?: string;
  /** The live runtime supplies the canonical tenant; fixture drawers keep their title. */
  readonly drawerTitle?: string;
  /** P14's non-service rail preserves every granted destination. */
  readonly allGrantedInRail?: boolean;
  /** The tone the mounted screen asked for; the drawer renders outside the shell root and carries it itself. */
  readonly tone?: LivingTone;
}

/** @internal The column, rail, drawer and bottom bar of `NavigationShell`; exported for its spec. */
export function ResponsiveNavigation({
  currentPath,
  destinations,
  folded,
  onFoldChange,
  hasPrimaryAction,
  navigationRole,
  restaurantName,
  identity,
  localViews = [],
  onNavigate,
  drawerClassName,
  drawerTitle = 'Áreas del Panel',
  allGrantedInRail = false,
  tone = 'dia',
}: ResponsiveNavigationProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  /* The navigation of the whole Panel (docs/decisions/2026-10-13-panel-living-redesign.md, ruling 9) is a 72 px rail of
     icons that opens into the same rail with its names. The names need room: from 841 px, and on a service screen
     (TPV, Cocina, Servicio) from 1201 px, the widths at which those screens had a wide column before. Narrower, the
     rail stays folded whatever was chosen, and its handle is not drawn. */
  const tooNarrow = useMediaQuery(hasPrimaryAction ? PANEL_V2_RESPONSIVE_MEDIA.serviceNavigationNamesHidden : PANEL_V2_RESPONSIVE_MEDIA.navigationToggleHidden);
  const named = !folded && !tooNarrow;
  /* Folded, every icon keeps its accessible name and the name is drawn beside it on hover and on keyboard focus. */
  const iconRail = allGrantedInRail && !named;
  const [railTip, setRailTip] = useState<RailTip | null>(null);
  const hideRailTip = () => setRailTip(null);
  const showRailTip = (target: EventTarget, rail: HTMLElement) => {
    // React carries the events of what the rail opens in a portal (the notices) up to the rail: those are not its own.
    const control = target instanceof Element && rail.contains(target) ? target.closest<HTMLElement>(RAIL_TIP_TARGETS) : null;
    const label = control === null ? '' : control.dataset.railTip ?? control.getAttribute('aria-label') ?? '';
    if (control === null || label === '') { setRailTip(null); return; }
    const box = control.getBoundingClientRect();
    setRailTip({ label, left: rail.getBoundingClientRect().right, top: box.top + box.height / 2 });
  };
  /* A press also focuses what it presses, and that focus asks for nothing: the name follows the pointer and the
     keyboard only. A finger cannot hover: where there is no hover the stylesheet writes the names under the icons. */
  const railPressed = useRef(false);
  const railTipHandlers = iconRail ? {
    onBlur: hideRailTip,
    onFocus: (event: FocusEvent<HTMLElement>) => {
      if (!railPressed.current && focusedByKeyboard(event.target)) showRailTip(event.target, event.currentTarget);
    },
    onPointerDown: () => { railPressed.current = true; hideRailTip(); },
    onPointerLeave: () => { railPressed.current = false; hideRailTip(); },
    onPointerOver: (event: ReactPointerEvent<HTMLElement>) => { if (event.pointerType !== 'touch') showRailTip(event.target, event.currentTarget); },
    onPointerUp: () => { railPressed.current = false; },
    onWheel: hideRailTip,
  } : {};
  /* D-160 ruling 7: the bottom bar and its sheet menu belong to touch devices,
     never to a computer, and never to a pixel width. Service shells (TPV,
     Cocina, Servicio) carry it too: wave C made their stylesheets reserve the
     bar's height under their fixed footers, so the hold the foundation kept
     until then is lifted. */
  const touchDevice = useMediaQuery(PANEL_V2_RESPONSIVE_MEDIA.touchDevice);
  const touchChrome = touchDevice;
  const modeSwitch = useSyncExternalStore(subscribePaseOptIn, paseOptedIn, paseNotOptedIn);
  const compactDestinations = roleDestinations(destinations, navigationRole);
  /* D-160 ruling 20 (report C20): a restricted shell lists its v20 `ROL_SCOPE`
     and nothing else — the rail, the wide column, the drawer and the bottom bar
     all read from the same set, as v20's own comment on `ROL_SCOPE` demands
     («si se separan, un aviso lleva a una pantalla que el rol no puede abrir»).
     Management is unaffected: `navigationScope` returns every granted
     destination for `gerencia`. Presentation only — `destinations` still comes
     from `visibleDestinations`, so admission and deep links are unchanged. */
  const listedDestinations = navigationScope(destinations, navigationRole);
  const railDestinations = allGrantedInRail ? listedDestinations : compactDestinations;
  const homeView = navigationRole === 'gerencia' ? localViews.find(({ id }) => id === 'home') : undefined;
  const boardView = localViews.find(({ id }) => id === 'board');
  /* D-160 ruling 17: «Home solo muestra el parte, recuerda, y board todo el
     resto incluido constructor.» A manager's navigation has two modes and the
     path is what picks them — Home mode on the Home local view's own path,
     Board mode on /app/board and on every destination route. v20 says the same
     thing through `navShowHome` / `navShowGestion` / `navShowDept` /
     `navShowOps`, all four gated on `view`. Employees have no Home local view;
     their single Board link keeps the overview reachable without exposing El
     Parte or changing the role-scoped destination list (ruling 1). */
  const homeMode = homeView !== undefined && currentPath === homeView.path;
  const litViewId = homeView !== undefined ? homeMode ? 'home' : 'board' : boardView?.id;
  const parteEntries = homeView === undefined ? [] : [parteEntry(homeView)];
  const listGroups = homeMode ? [HOME_NAVIGATION_GROUP] : PANEL_DESTINATION_GROUPS;
  const listEntries = homeMode ? parteEntries : listedDestinations.map(destinationEntry);
  const railEntries = homeMode ? parteEntries : railDestinations.map(destinationEntry);
  /* The bottom bar is the one surface that does not change with the mode: the
     manager's Home, then TPV, Servicio and Cocina as the staff drawings list
     them (whichever the role is listed), then «Más» with everything else. */
  const barDestinations = bottomBarDestinations(destinations, navigationRole);
  /* On a touch device the bar ends with «Más», and with the Day/Night switch on a screen that opted in. */
  const compactCount = barDestinations.length + (homeView ? 1 : 0) + (touchChrome ? (modeSwitch ? 2 : 1) : 0);
  const bottomStyle = { '--destination-count': Math.max(compactCount, 1) } as CSSProperties;
  const navigationProps = onNavigate === undefined ? {} : { onNavigate };
  const drawerNavigationProps = onNavigate === undefined ? {} : {
    onNavigate: (path: string) => { setDrawerOpen(false); onNavigate(path); },
  };
  const brand = restaurantName ?? identity?.restaurantName ?? 'Restaurante';
  const drawerTone = tone === 'noche' ? { 'data-shell-tone': 'noche' } as const : {};

  return (
    <Dialog.Root onOpenChange={setDrawerOpen} open={drawerOpen}>
      <nav aria-label="Áreas del Panel" className="app-navigation" {...railTipHandlers}>
        {/* One line, ellipsised; the full tenant name stays readable in the title, or beside the rail's mark. */}
        <span className="app-navigation__brand restaurant-name" {...(iconRail ? { 'data-rail-tip': brand } : { title: brand })}>{brand}</span>
        <LocalViewLinks currentPath={currentPath} litViewId={litViewId} travels views={localViews} {...navigationProps} />
        <NavigationList className="app-navigation__list--wide" currentPath={currentPath} entries={listEntries} groups={listGroups} grouped {...navigationProps} />
        <NavigationList className="app-navigation__list--rail" compact currentPath={currentPath} entries={railEntries} groups={listGroups} {...navigationProps} />
        {!homeMode && !allGrantedInRail && listedDestinations.length > compactDestinations.length && (
          <Dialog.Trigger asChild>
            <button className="btn btn--secondary app-navigation__more" type="button">
              <span aria-hidden="true">•••</span>
              <span className="app-navigation__label">Más</span>
            </button>
          </Dialog.Trigger>
        )}
        {/* The handle on the rail's edge: it opens the rail into its names and folds it back. */}
        <button
          aria-expanded={named}
          aria-label={named ? 'Plegar el menú' : 'Desplegar el menú'}
          className="app-navigation__fold"
          data-hit=""
          onClick={() => onFoldChange(!folded)}
          {...(named ? { title: 'Plegar el menú' } : {})}
          type="button"
        >
          <svg aria-hidden="true" fill="none" focusable="false" viewBox="0 0 24 24"><path d="m10 7 5 5-5 5" /></svg>
        </button>
        {/* The Day/Night switch (docs/decisions/2026-10-31-panel-day-and-night-modes.md), above the person and the bell. */}
        {modeSwitch && <ModeSwitch />}
        {!drawerOpen && identity !== undefined && <IdentityChrome {...identity} railTips={iconRail} />}
      </nav>
      {iconRail && railTip !== null && <span aria-hidden="true" className="app-navigation__tip"
        style={{ transform: `translate(${railTip.left}px, ${railTip.top}px)` }}><span>{railTip.label}</span></span>}

      {!touchChrome && (
        <header className="mobile-header">
          <Dialog.Trigger asChild>
            <button aria-label="Abrir navegación" className="btn btn--secondary mobile-header__menu" type="button">
              {allGrantedInRail ? <NavigationToggleMark /> : <span aria-hidden="true">☰</span>}
            </button>
          </Dialog.Trigger>
          <strong className="restaurant-name" title={brand}>{brand}</strong>
        </header>
      )}

      <nav
        aria-label="Áreas principales"
        className="bottom-navigation"
        data-visible={(touchChrome || !hasPrimaryAction) && compactCount > 0}
        style={bottomStyle}
      >
        {homeView && <a aria-current={currentPath === homeView.path ? 'page' : undefined} aria-label="Home" className="bottom-navigation__link" href={homeView.path}
          onClick={(event) => { if (onNavigate && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onNavigate(homeView.path); } }}
          {...prefetchOnIntent(homeView.path)}><LocalViewMark id="home" /><span>Home</span></a>}
        {barDestinations.map((destination) => (
          <a
            aria-current={currentPath === destination.path ? 'page' : undefined}
            aria-label={destination.label}
            className="bottom-navigation__link"
            href={destination.path}
            key={destination.workspaceId}
            onClick={(event) => { if (onNavigate && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); onNavigate(destination.path); } }}
            {...prefetchOnIntent(destination.path)}
          >
            <DestinationMark destination={destination} />
            <span>{destination.shortLabel}</span>
          </a>
        ))}
        {touchChrome && (
          <Dialog.Trigger asChild>
            <button aria-label="Más" className="bottom-navigation__link" type="button">
              <MoreMark />
              <span>Más</span>
            </button>
          </Dialog.Trigger>
        )}
        {touchChrome && modeSwitch && <ModeSwitch compact />}
      </nav>

      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" {...drawerTone} />
        <Dialog.Content {...drawerTone} aria-describedby="navigation-drawer-description" className={['navigation-drawer', touchChrome ? 'navigation-drawer--sheet' : '', drawerClassName].filter(Boolean).join(' ')}>
          <div className="navigation-drawer__header">
            <Dialog.Title className="dialog-content__title" title={drawerTitle}>{drawerTitle}</Dialog.Title>
            <Dialog.Close asChild>
              <button aria-label="Cerrar navegación" className="btn btn--secondary" type="button">
                ×
              </button>
            </Dialog.Close>
          </div>
          <Dialog.Description hidden id="navigation-drawer-description">Destinos concedidos.</Dialog.Description>
          <LocalViewLinks closeAfterNavigate currentPath={currentPath} litViewId={litViewId} views={localViews} {...drawerNavigationProps} />
          <NavigationList
            className="navigation-drawer__list"
            closeAfterNavigate
            currentPath={currentPath}
            entries={listEntries}
            groups={listGroups}
            grouped
            {...drawerNavigationProps}
          />
          {drawerOpen && identity !== undefined && <IdentityChrome {...identity} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* The navigation's form, the rail or the rail with its names, is one choice for the whole Panel: every shell reads it
   here, and it is remembered on this device. A window that cannot store keeps it for the visit. */
const NAVIGATION_FORM_KEY = 'exylon.panel.navigation';
const navigationFormListeners = new Set<() => void>();
let namedForThisVisit = false;
const navigationNamed = (): boolean => {
  const stored = readDeviceChoice(NAVIGATION_FORM_KEY);
  return stored === null ? namedForThisVisit : stored === 'names';
};
const chooseNavigationNamed = (named: boolean): void => {
  namedForThisVisit = named;
  writeDeviceChoice(NAVIGATION_FORM_KEY, named ? 'names' : 'icons');
  for (const listener of navigationFormListeners) listener();
};
const subscribeNavigationForm = (listener: () => void): (() => void) => {
  navigationFormListeners.add(listener);
  return () => { navigationFormListeners.delete(listener); };
};

/** Home and Board, the local views of a restaurant session; read them through
 * `panelLocalViews`. */
const HOME_BOARD_VIEWS = Object.freeze([
  { id: 'home', label: 'Home', path: '/app/home' },
  { id: 'board', label: 'Board', path: '/app/board' },
] as const satisfies readonly PanelLocalView[]);

/* docs/decisions/2026-10-30-manager-board-opens-analitica.md: a manager's Board opens Analítica, never the page
   selector; only where Analítica is granted, else it keeps the selector. Employees keep the selector. */
const MANAGER_BOARD_PATH = '/app/analytics';
const MANAGER_ANALYTICS_VIEWS = Object.freeze([
  HOME_BOARD_VIEWS[0],
  { id: 'board', label: 'Board', path: MANAGER_BOARD_PATH },
] as const satisfies readonly PanelLocalView[]);

/** Both local views for gerencia (its Board on Analítica when granted), Board
 * alone for a role with its own destinations, none when navigation is off. */
export function panelLocalViews(access: PanelAccessModel): readonly PanelLocalView[] {
  if (!access.navigationEnabled) return [];
  const destinations = visibleDestinations(access);
  if (access.navigationRole === 'gerencia') {
    return destinations.some(({ path }) => path === MANAGER_BOARD_PATH) ? MANAGER_ANALYTICS_VIEWS : HOME_BOARD_VIEWS;
  }
  return roleDestinations(destinations, access.navigationRole).length > 0 ? HOME_BOARD_VIEWS.slice(1) : [];
}

/** Where `/app/board` sends a session whose Board opens elsewhere (a manager's
 * Analítica), or null where the Board page itself is the Board. */
export function boardPathRedirect(views: readonly PanelLocalView[]): PanelLocalView['path'] | null {
  const board = views.find(({ id }) => id === 'board');
  return board === undefined || board.path === '/app/board' ? null : board.path;
}

export interface NavigationShellProps {
  /** The restaurant access the column lists; without one (a franchise or
   * platform session on a shared page) `tenantChrome` stands in for it. */
  readonly access: PanelAccessModel | null;
  readonly tenantChrome?: ReactNode;
  readonly currentPath: string;
  readonly localViews: readonly PanelLocalView[];
  readonly identity: IdentityChromeProps;
  readonly restaurantName: string;
  readonly onNavigate: (path: string) => void;
  readonly hasPrimaryAction?: boolean;
  readonly bottomNavigation: boolean;
  readonly drawerTitle?: string;
  /** Classes added to the shell root and to the drawer (the runtime's own skin). */
  readonly className?: string;
  readonly drawerClassName?: string;
  readonly tenantKind?: string;
  /** Above the content and outside its freeze (session and route status). */
  readonly status?: ReactNode;
  /** Present when the content freezes while Identity re-verifies. */
  readonly frozen?: boolean;
  readonly contentClassName?: string;
  readonly contentAttributes?: Readonly<Record<`data-${string}`, string | undefined>>;
  readonly children: ReactNode;
}

/**
 * PS-06 · the one navigation shell of the Panel: the live runtime's
 * destination screens, Home/Board and the proof gallery all draw this root —
 * `app-shell panel-shell` around `ResponsiveNavigation` with every
 * granted destination and the Home/Board local views.
 *
 * Its navigation is the rail of icons on every page (ruling 9 of
 * docs/decisions/2026-10-13-panel-living-redesign.md). The handle on the rail's edge opens it into the same rail with
 * its names and folds it back; the page beside it gives or takes that width. The change is drawn by the browser as one
 * movement of two pictures (the navigation widening, the page sliding), so nothing is laid out frame by frame; where
 * the browser cannot, or the person asked for less motion, it lands at once.
 */
export function NavigationShell({
  access, tenantChrome, currentPath, localViews, identity, restaurantName, onNavigate, hasPrimaryAction = false,
  bottomNavigation, drawerTitle, className, drawerClassName, tenantKind,
  status, frozen, contentClassName, contentAttributes, children,
}: NavigationShellProps) {
  const named = useSyncExternalStore(subscribeNavigationForm, navigationNamed, () => false);
  const root = useRef<HTMLDivElement>(null);
  const setFolded = useCallback((folded: boolean) => {
    const shell = root.current;
    const moves = shell !== null && typeof document.startViewTransition === 'function' && !prefersReducedMotion();
    if (!moves) { chooseNavigationNamed(!folded); return; }
    // The two pictures are named only while they move, so nothing else of the page is ever captured with them.
    shell.setAttribute('data-navigation-moving', '');
    const done = () => shell.removeAttribute('data-navigation-moving');
    try {
      document.startViewTransition(() => { flushSync(() => chooseNavigationNamed(!folded)); }).finished.then(done, done);
    } catch { done(); chooseNavigationNamed(!folded); }
  }, []);
  /* A night screen (Tarjetas, the Constructor; docs/decisions/2026-10-13-panel-living-redesign.md) asks for the night tone while
     it is mounted, so the navigation beside it is not a bright strip. Counted, so a remount never leaves the shell dark. */
  const [nightRequests, setNightRequests] = useState(0);
  const requestTone = useCallback<ShellToneRequest>((tone) => {
    if (tone !== 'noche') return () => undefined;
    setNightRequests((count) => count + 1);
    return () => setNightRequests((count) => count - 1);
  }, []);
  const tone: LivingTone = nightRequests > 0 ? 'noche' : 'dia';
  /* The highlight moves at the tap: the router keeps the old screen while the new one loads, so the navigation shows the
     path it was asked for until the route lands (or, if the shell refused it, for a few seconds). */
  const [tapped, setTapped] = useState<Readonly<{ from: string; to: string }> | null>(null);
  const shownPath = tapped !== null && tapped.from === currentPath ? tapped.to : currentPath;
  useEffect(() => {
    if (tapped === null || tapped.from !== currentPath) return undefined;
    const timer = window.setTimeout(() => setTapped(null), 8_000);
    return () => window.clearTimeout(timer);
  }, [tapped, currentPath]);
  const navigateFromRail = (path: string) => {
    if (path !== currentPath) setTapped({ from: currentPath, to: path });
    onNavigate(path);
  };
  /* After login, the visible destinations' screens load while the browser is idle, so the first tap on each opens it
     without waiting for its code. */
  const destinationPaths = access === null ? '' : [...localViews.map((view) => view.path),
    ...visibleDestinations(access).map((destination) => destination.path)].join('\n');
  useEffect(() => (destinationPaths === '' ? undefined : prefetchScreensWhenIdle(destinationPaths.split('\n'))), [destinationPaths]);
  const content = <main className={contentClassName === undefined ? 'app-shell__content' : `app-shell__content ${contentClassName}`} {...contentAttributes}>
    {children}
  </main>;
  return <ShellToneContext.Provider value={requestTone}><div className={className === undefined ? 'app-shell panel-shell' : `app-shell panel-shell ${className}`}
    data-bottom-navigation={bottomNavigation} data-navigation={named ? 'names' : 'icons'} data-service={hasPrimaryAction} data-shell=""
    {...(tone === 'noche' ? { 'data-shell-tone': 'noche' } : {})}
    {...(tenantKind === undefined ? {} : { 'data-runtime-tenant': tenantKind })} ref={root}>
    {access ? <NavigationTone tone={tone}><ResponsiveNavigation allGrantedInRail currentPath={shownPath} destinations={visibleDestinations(access)}
      drawerClassName={drawerClassName === undefined ? 'panel-shell__drawer' : `panel-shell__drawer ${drawerClassName}`}
      {...(drawerTitle === undefined ? {} : { drawerTitle })} folded={!named} hasPrimaryAction={hasPrimaryAction}
      identity={identity} localViews={localViews} navigationRole={access.navigationRole} onFoldChange={setFolded}
      onNavigate={navigateFromRail} restaurantName={restaurantName} tone={tone} /></NavigationTone> : tenantChrome}
    <div className="app-shell__main">
      {status}
      {frozen === undefined ? content : <FrozenInteractionBoundary frozen={frozen} showBanner={false}>{content}</FrozenInteractionBoundary>}
    </div>
  </div></ShellToneContext.Provider>;
}

export interface SessionBoundaryProps extends PropsWithChildren {
  readonly expired: boolean;
  readonly personName: string;
  readonly avatarUrl?: string | null;
  readonly roleLabel?: string;
  readonly onAuthenticate: (request: ReauthenticationRequest) => Promise<AuthenticationOutcome>;
  readonly onSecondFactorRequired: () => void;
  readonly onDifferentPerson: () => void;
}

/**
 * «Sigues aquí, pero hay que confirmar quién eres»: the living dialog over the dimmed, frozen screen (a sheet from the
 * foot on a phone). The person's face and «{nombre} · {rol}» lead; only their credential is asked, and it is focused;
 * «No soy …» leaves to a clean login. It cannot be dismissed, and it is `aria-modal`. Reauthenticating keeps this
 * screen and its draft (docs/decisions/2026-09-30-panel-reauthentication-keeps-unsent-work.md).
 */
export function SessionBoundary({
  expired,
  personName,
  avatarUrl,
  roleLabel,
  onAuthenticate,
  onSecondFactorRequired,
  onDifferentPerson,
  children,
}: SessionBoundaryProps) {
  const avatarLetter = Array.from(personName.trim())[0]?.toLocaleUpperCase('es-ES') ?? '·';
  return (
    <>
      <div aria-hidden={expired || undefined} inert={expired || undefined}>
        {children}
      </div>
      <MotionProvider features="layout">
        <DialogCard className="acc-dialog acc-dialog--reauth session-boundary__dialog"
          description="Por seguridad, tu sesión ha caducado. No has perdido nada: vuelve a entrar y sigues justo donde estabas."
          dismissible={false} onOpenChange={keepSessionDialog} open={expired}
          onOpenAutoFocus={(event) => {
            // Focus the credential without selecting what is already in it (Radix selects an input it focuses).
            event.preventDefault();
            if (event.currentTarget instanceof HTMLElement) event.currentTarget.querySelector<HTMLInputElement>('input')?.focus();
          }}
          title="Sigues aquí, pero hay que confirmar quién eres">
          <div className="acc-reauth__who" ref={markModal}>
            <StaffAvatar className="acc-reauth__face session-boundary__avatar" fallback={avatarLetter} imageUrl={avatarUrl} />
            <span className="acc-reauth__person session-boundary__person">{personName}{roleLabel ? ` · ${roleLabel.toLocaleLowerCase('es-ES')}` : ''}</span>
          </div>
          <ReauthenticationForm
            onAuthenticate={onAuthenticate}
            onSecondFactorRequired={onSecondFactorRequired}
          />
          <button className="lv-linkbtn lv-press acc-reauth__other session-boundary__different-person" data-source-control="chrome-DOM-C005"
            onClick={onDifferentPerson} type="button">
            No soy {personName}
          </button>
        </DialogCard>
      </MotionProvider>
    </>
  );
}

/** The expired session's dialog is closed by the runtime only (a confirmed identity), never by Escape or the scrim. */
const keepSessionDialog = (): void => undefined;
